import type { OvStop } from './ovQuality'

/**
 * `poi_type` is a partition: ATLAS types each stop by its highest mode, so a
 * bus-and-tram interchange appears once. The previous implementation matched
 * substrings of swisstopo's `verkehrsmittel_de`, which both double-counted
 * multi-mode stops and matched "Standseilbahn" as a railway.
 */
export interface CategorizedStops {
  bus: OvStop[]
  tram: OvStop[]
  train: OvStop[]
  boat: OvStop[]
  cable: OvStop[]
  other: OvStop[]
}

type Bucket = keyof CategorizedStops

const BUCKET_BY_POI_TYPE: Record<string, Bucket> = {
  atlas_bus_stop: 'bus',
  atlas_tram_stop: 'tram',
  atlas_metro_station: 'tram',
  atlas_train_station: 'train',
  atlas_boat_stop: 'boat',
  atlas_cable_car_stop: 'cable',
}

export const categorizeTransitStops = (stops: OvStop[]): CategorizedStops => {
  const buckets: CategorizedStops = {
    bus: [],
    tram: [],
    train: [],
    boat: [],
    cable: [],
    other: [],
  }

  for (const stop of stops) {
    buckets[BUCKET_BY_POI_TYPE[stop.poi_type ?? ''] ?? 'other'].push(stop)
  }

  return buckets
}

/** Stops arrive distance-sorted, so the first match is the nearest. */
export const nearestStopOfType = (
  stops: OvStop[],
  poiTypes: string[]
): OvStop | null =>
  stops.find((stop) => poiTypes.includes(stop.poi_type ?? '')) ?? null

export const countStopsWithin = (stops: OvStop[], metres: number): number =>
  stops.filter((stop) => stop.distance <= metres).length

export type TransitMode = 'BUS' | 'TRAM' | 'METRO' | 'TRAIN' | 'BOAT' | 'CABLE'

/** A stop's highest mode only - the fallback source, never the primary one. */
const MODE_BY_POI_TYPE: Record<string, TransitMode> = {
  atlas_bus_stop: 'BUS',
  atlas_tram_stop: 'TRAM',
  atlas_metro_station: 'METRO',
  atlas_train_station: 'TRAIN',
  atlas_boat_stop: 'BOAT',
  atlas_cable_car_stop: 'CABLE',
}

/**
 * Every ATLAS mode code, collapsed onto the six modes the app renders.
 * RACK_RAILWAY is heavy rail; CABLE_RAILWAY (funicular), CABLE_CAR and
 * CHAIRLIFT are all cable-drawn.
 */
const TRANSIT_MODE_BY_ATLAS_MODE: Record<string, TransitMode> = {
  BUS: 'BUS',
  TRAM: 'TRAM',
  METRO: 'METRO',
  TRAIN: 'TRAIN',
  RACK_RAILWAY: 'TRAIN',
  BOAT: 'BOAT',
  CABLE_RAILWAY: 'CABLE',
  CABLE_CAR: 'CABLE',
  CHAIRLIFT: 'CABLE',
}

/**
 * Does this mode actually call at this stop?
 *
 * Deliberately overlapping, unlike categorizeTransitStops: a tram+bus
 * interchange answers true for both. `poi_type` records only a stop's highest
 * mode, so it is the fallback for payloads with no modes array, not the source.
 */
export const stopServesMode = (stop: OvStop, mode: TransitMode): boolean => {
  const modes = stop.transit?.modes
  if (modes && modes.length > 0) {
    return modes.some(
      (atlasMode) => TRANSIT_MODE_BY_ATLAS_MODE[atlasMode] === mode
    )
  }
  return MODE_BY_POI_TYPE[stop.poi_type ?? ''] === mode
}
